/**
 * Proofing Service API Client Setup
 *
 * This file configures global authentication handling for the auto-generated
 * client with automatic, transparent token refresh on authentication failures.
 *
 * Token Refresh Flow:
 * 1. A request fails with a 401 indicating the token expired (REFRESH_NEEDED).
 * 2. The wrapper detects the refreshable auth error.
 * 3. A token refresh is initiated (concurrent failures share the same refresh
 *    via tokenRefreshService deduplication).
 * 4. After a successful refresh, the original request is re-run through the
 *    hey-api client, which rebuilds the Request (and its body) from the same
 *    options and returns/throws the real result to the caller.
 * 5. If the refresh fails, the retry still 401s, or the error is not
 *    refreshable, the user is redirected to login (preserving the return URL).
 *
 * Why wrap the client methods instead of using an error interceptor:
 * hey-api's error interceptor can only *replace the error* — any value it
 * returns is treated as `finalError` and is thrown to callers that use
 * `throwOnError: true` (the default for every generated SDK function). That
 * means a retried request could never surface as a success from inside the
 * error interceptor. Re-running the request through the client's own method
 * lets the recovered response flow back as a normal success (correct body,
 * response parsing and throwOnError handling), which is what keeps users from
 * being logged out mid-correction (issue #616).
 *
 * Loop Prevention:
 * - A request is retried at most once after a refresh.
 * - If the retry also fails with a 401, the user is redirected to login.
 *
 * Base URL and credentials are configured via createClientConfig in
 * proofingService-client-config.ts. DO NOT modify client.gen.ts directly as it
 * will be overwritten. Use the SDK functions which automatically use this
 * configured client.
 */

import {
  handleAuthError,
  isAuthError,
  isRefreshableAuthError,
  isTokenExpired,
} from "@/lib/auth/authErrorHandler";
import { tokenRefreshService } from "@/lib/auth/tokenRefreshService";
import type { HeyApiError } from "@/lib/types";
import type { RequestOptions } from "../../proofingService-client/client/types.gen";
import { client } from "../../proofingService-client/client.gen";

/**
 * HTTP method functions on the hey-api client that the generated SDK uses.
 * Each one is wrapped so requests transparently recover from token expiry.
 */
const METHODS = [
  "connect",
  "delete",
  "get",
  "head",
  "options",
  "patch",
  "post",
  "put",
  "trace",
] as const;

type MethodName = (typeof METHODS)[number];
type RequestFn = (options: RequestOptions) => Promise<unknown>;

/**
 * Extract the error body and (optional) Response from either a thrown value
 * (throwOnError: true) or a returned `{ error, response }` object
 * (throwOnError: false), so a 401 can be classified consistently.
 */
function extractAuthError(value: unknown): { error: unknown; response?: Response } {
  if (value && typeof value === "object" && "error" in value) {
    const { error, response } = value as { error?: unknown; response?: Response };
    return { error, response };
  }
  return { error: value };
}

/**
 * Determine whether a failed request (thrown or returned) is a token-expiry
 * 401 that should be retried after a refresh.
 */
function shouldRefresh(value: unknown): boolean {
  const { error, response } = extractAuthError(value);
  return isRefreshableAuthError(error, response) && isTokenExpired(error);
}

/**
 * Whether a failed request was a 401 at all (refreshable or not).
 */
function isAuthFailure(value: unknown): boolean {
  const { error, response } = extractAuthError(value);
  return isAuthError(error, response);
}

/**
 * Wrap a single client method so that a refreshable 401 triggers a silent
 * token refresh and a single retry of the original request.
 *
 * The retry re-invokes the original method with the same options, so hey-api
 * rebuilds the Request (including the body from `opts`) and applies the
 * caller's original `throwOnError`/`responseStyle`. This makes the recovered
 * response flow back to the caller as a normal success.
 */
function wrapMethod(method: MethodName): void {
  const original = client[method] as unknown as RequestFn;

  const wrapped: RequestFn = async (options: RequestOptions) => {
    try {
      return await original(options);
    } catch (error) {
      if (!shouldRefresh(error)) {
        // Non-refreshable 401 → redirect to login. Other errors propagate.
        if (isAuthFailure(error)) {
          handleAuthError(extractAuthError(error).error as HeyApiError);
        }
        throw error;
      }

      // Refresh the token (concurrent failures share the same refresh) and
      // retry the request exactly once.
      const refreshed = await tokenRefreshService.refreshToken();
      if (!refreshed) {
        handleAuthError(extractAuthError(error).error as HeyApiError);
        throw error;
      }

      try {
        return await original(options);
      } catch (retryError) {
        // Retry still failed with 401 → genuine re-auth required.
        if (isAuthFailure(retryError)) {
          handleAuthError(extractAuthError(retryError).error as HeyApiError);
        }
        throw retryError;
      }
    }
  };

  (client as Record<MethodName, RequestFn>)[method] = async (options: RequestOptions) => {
    // Methods may also be called with throwOnError: false, in which case the
    // failure is returned (as { error, response }) rather than thrown.
    if (options?.throwOnError === false) {
      return wrapReturnedError(original, options);
    }
    return wrapped(options);
  };
}

/**
 * Retry handling for callers using `throwOnError: false`, where a 401 is
 * returned as `{ error, response }` instead of thrown.
 *
 * hey-api only returns `{ error, response }` when `responseStyle` is `"fields"`
 * (the default). With `responseStyle: "data"` an error is returned as
 * `undefined` (see client.gen.ts), which carries no status/body to classify a
 * 401. So we always run the request internally with `responseStyle: "fields"`
 * to detect a refreshable 401, then map the recovered result back to the
 * caller's requested `responseStyle`.
 */
async function wrapReturnedError(original: RequestFn, options: RequestOptions): Promise<unknown> {
  const callerStyle = options.responseStyle ?? "fields";
  const probeOptions =
    callerStyle === "fields" ? options : { ...options, responseStyle: "fields" as const };

  const toCallerStyle = (value: unknown): unknown => {
    if (callerStyle !== "data") {
      return value;
    }
    // Caller asked for `data` style: unwrap the `{ data }` field (error → data
    // is `undefined`, matching hey-api's own `responseStyle: "data"` contract).
    return value && typeof value === "object" && "data" in value
      ? (value as { data?: unknown }).data
      : undefined;
  };

  const result = await original(probeOptions);

  if (!isAuthFailure(result)) {
    return toCallerStyle(result);
  }

  if (!shouldRefresh(result)) {
    handleAuthError(extractAuthError(result).error as HeyApiError);
    return toCallerStyle(result);
  }

  const refreshed = await tokenRefreshService.refreshToken();
  if (!refreshed) {
    handleAuthError(extractAuthError(result).error as HeyApiError);
    return toCallerStyle(result);
  }

  const retried = await original(probeOptions);
  if (isAuthFailure(retried)) {
    handleAuthError(extractAuthError(retried).error as HeyApiError);
  }
  return toCallerStyle(retried);
}

for (const method of METHODS) {
  wrapMethod(method);
}

export const proofingClient = client;
